import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Id } from '@shared/types';
import { api, ApiError, type AssetFilter } from './api';

function retryUnlessAuth(count: number, err: unknown) {
  if (err instanceof ApiError && (err.isAuthError || err.status === 404)) return false;
  return count < 2;
}

export const keys = {
  me: ['me'] as const,
  folders: ['folders'] as const,
  assets: (f: AssetFilter) => ['assets', f] as const,
  asset: (id: Id) => ['asset', id] as const,
  search: (p: unknown) => ['search', p] as const,
  facets: ['facets'] as const,
  prompt: ['tag-prompt'] as const,
  queue: ['tag-queue'] as const,
  stats: ['library-stats'] as const,
  favIds: ['favourite-ids'] as const,
  users: ['users'] as const,
};

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: () => api.me().then((r) => r.user), retry: retryUnlessAuth, staleTime: 15 * 60_000 });
}
export function useFolders() {
  return useQuery({ queryKey: keys.folders, queryFn: () => api.folders(), retry: retryUnlessAuth, staleTime: 60_000 });
}
export function useAssets(f: AssetFilter) {
  return useQuery({ queryKey: keys.assets(f), queryFn: () => api.assets(f), retry: retryUnlessAuth, staleTime: 15_000 });
}
export function useAsset(id: Id | undefined) {
  return useQuery({ queryKey: keys.asset(id ?? 0), queryFn: () => api.asset(id as Id).then((r) => r.asset), enabled: id !== undefined, retry: retryUnlessAuth });
}
export function useSearch(params: Record<string, unknown>, enabled = true) {
  return useQuery({ queryKey: keys.search(params), queryFn: () => api.search(params), enabled, retry: retryUnlessAuth, staleTime: 15_000 });
}
export function useFacets() {
  return useQuery({ queryKey: keys.facets, queryFn: () => api.facets(), retry: retryUnlessAuth, staleTime: 60_000 });
}

export function useUpload(folderId: Id | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.upload(folderId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: keys.folders });
    },
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: Id | null }) => api.createFolder(name, parentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.folders }),
  });
}

export function useUpdateMetadata(id: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (metadata: Record<string, unknown>) => api.updateMetadata(id, metadata),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.asset(id) });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useTagNow(id: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.tagNow(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.asset(id) }),
  });
}
export function useConfirmTags(id: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (confirmed: boolean) => api.confirmTags(id, confirmed),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.asset(id) });
      void qc.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

export function useLibraryStats() {
  return useQuery({ queryKey: keys.stats, queryFn: () => api.libraryStats(), retry: retryUnlessAuth, staleTime: 30_000 });
}
export function useFavouriteIds() {
  return useQuery({ queryKey: keys.favIds, queryFn: () => api.favouriteIds().then((r) => new Set(r.ids)), retry: retryUnlessAuth, staleTime: 30_000 });
}
export function useToggleFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => api.toggleFavourite(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.favIds });
      void qc.invalidateQueries({ queryKey: keys.stats });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useUsers() {
  return useQuery({ queryKey: keys.users, queryFn: () => api.users().then((r) => r.items), retry: retryUnlessAuth });
}
export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: Id; isActive: boolean }) => api.setUserActive(id, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}
export function useSetUserAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isAdmin }: { id: Id; isAdmin: boolean }) => api.setUserAdmin(id, isAdmin),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export function useCreateFolderAt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: Id | null }) => api.createFolder(name, parentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.folders }),
  });
}
export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: Id; name: string }) => api.renameFolder(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.folders }),
  });
}
export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => api.deleteFolder(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.folders });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.logout(), onSuccess: () => qc.clear() });
}
